import type { ReactNode } from 'react';
import type { UseAppForm } from '../../form';
import type { OptionKeyDownFn, OptionValue } from './OptionField/OptionField';

import { useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import * as Y from 'yjs';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';

import { PlusCircleOutlineIcon } from '../../../images/shapes';

import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

interface OptionsFieldProps {
  /**
   * Options field label
   */
  label?: string;
  /**
   * If options should be rendered as key-value pair (default it is an array of string)
   */
  useOptionsWithLabels?: boolean;
  /**
   * Component test id
   */
  testId?: string;
  /**
   * Additional container classes
   */
  additionalClasses?: string;
  /**
   * Help text under block input
   */
  helpText?: ReactNode;
  /**
   * If field is required
   */
  required?: boolean;
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
}

const OptionsField = ({
  label = '',
  useOptionsWithLabels = false,
  additionalClasses = '',
  helpText = '',
  required = false,
  disabled = false,
  readonly = false,
  testId = '',
}: OptionsFieldProps) => {
  const field = useFieldContext<OptionValue[]>();
  const form = useFormContext() as unknown as UseAppForm;
  const [focusIdx, setFocusIdx] = useState<number>();
  const yjsField = useYjsField('array');

  const errors = useFieldErrors(field.name);

  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const onBlur = useCallback(() => field.handleBlur(), [field]);

  const handleAddOption = () => {
    field.pushValue(
      useOptionsWithLabels
        ? {
            value: '',
            label: '',
          }
        : '',
    );
    onBlur();

    yjsField.insert(
      useOptionsWithLabels
        ? new Y.Map([
            ['value', ''],
            ['label', ''],
          ])
        : new Y.Text(''),
    );
  };

  const handleOrderUp = (idx: number) => {
    field.swapValues(idx - 1, idx);
    yjsField.swap(idx - 1, idx);
  };

  const handleOrderDown = (idx: number) => {
    field.swapValues(idx + 1, idx);
    yjsField.swap(idx + 1, idx);
  };

  const handleDeleteOption = async (idx: number) => {
    field.removeValue(idx);
    yjsField.delete(idx);
  };

  const handleKeyDownAction: OptionKeyDownFn = (
    e,
    option,
    currentValue,
    idx,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
      setFocusIdx(idx + 1);
    } else if (e.key === 'Backspace' && option === '') {
      e.preventDefault();
      handleDeleteOption(idx);
      if (currentValue.length > 1) setFocusIdx(idx === 0 ? idx + 1 : idx - 1);
    } else if (e.key === 'ArrowUp' && idx > 0) {
      e.preventDefault();
      handleOrderUp(idx);
    } else if (e.key === 'ArrowDown' && idx < currentValue.length - 1) {
      e.preventDefault();
      handleOrderDown(idx);
    }
  };

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
    >
      {label && (
        <label className="text-sm text-slate-400 dark:text-slate-200 mb-1">
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      {value && value.length > 0 && (
        <div className="mb-2 space-y-2">
          {value.map((option, idx, currentValue) => {
            const keyValue = idx;
            const fieldName = `${field.name}[${idx}]`;
            return (
              <form.AppField key={fieldName} name={fieldName}>
                {(subField) => (
                  <subField.OptionField
                    key={keyValue}
                    option={option}
                    idx={idx}
                    currentValue={currentValue}
                    focusIdx={focusIdx}
                    onKeyDown={handleKeyDownAction}
                    onUp={handleOrderUp}
                    onDown={handleOrderDown}
                    onDelete={handleDeleteOption}
                    disabled={disabled}
                    readonly={readonly}
                    useOptionsWithLabels={useOptionsWithLabels}
                    testId={testId}
                  />
                )}
              </form.AppField>
            );
          })}
        </div>
      )}
      <Button
        onClick={handleAddOption}
        buttonColor="blueBordered"
        iconImage={<PlusCircleOutlineIcon className="w-4 min-w-4" />}
        additionalClasses="px-4"
        disabled={disabled || readonly}
        {...getTestProps(testId, 'add-option', 'testId')}
      />
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={errors}
        testId={testId}
      />
    </div>
  );
};

export default OptionsField;
